import { Injectable, OnDestroy } from '@angular/core';
import { CONSTANTS } from '../../../Constants/Constants';
import { IReportData } from '../../../models/reports/IReportData';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { TranslateService } from '@ngx-translate/core';
import { CommonDataService } from '../../common-data.service';
import { ITableData } from '../../../models/reports/ITableData';
import { LaborMetaData, SQWebArea, SQWebSystem } from '../../../models/dtos';
import { IData } from '../../../models/reports/IData';
import { ReportTextAlignment } from '../../../models/reports/ReportTextAlignment';
import { SQWebAreaChildren } from '../../../models/SQWebAreaChildren';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { FireEstimateAnalysisType } from '../../../models/enum';
import { MultilingualCurrencyPipe } from '../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe';
import { roundToPrecision } from '../../../../types/number-extensions';

type DataValue = string | number | boolean | number[] | Date;

@Injectable()
export class FireEstimatingService implements OnDestroy {

  private fireEstimatingData: ITableData[] = [];
  private projectSellPrice: number = 0;
  private subcontractorCostPrice: number = 0;
  private siteSize: number = 0;
  private addressableDevices: number = 0;
  private notificationAppliances: number = 0;
  private readonly FIXED_FIRST_COLUMN_WIDTH: string = '250';

  constructor(private readonly commonDataService: CommonDataService,
    private readonly translate: TranslateService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly multiLingualPipe: MultilingualCurrencyPipe) { }

  generateReport(reportId: ReportList, pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT, internalReport: boolean = true) {
    const reportName = this.reportsCommonService.getSelectedReportName();
    let reportData: IReportData;
    const reportHeaderData = this.commonDataService.getReportHeaderData();
    reportData = {
      selectionName: this.translate.instant(reportName),
      reportTitle: this.translate.instant(reportName),
      reportTitles: {
        data: this.reportsCommonService.generateReportTitles(reportHeaderData)
      },
      tableData: this.generateTableData(),
      pageOrientation: pageOrientation,
      internalReport: internalReport
    }
    this.reportsCommonService.setSelectedReport(reportData);
  }

  generateTableData() {
    this.clearFireEstimatingData();
    const laborMetaData = this.commonDataService.getLaborMetaData();
    const estimateMetaData = this.commonDataService.getEstimateMetaData();
    const otherMetaData = this.commonDataService.getOtherMetaData();
    const sqWebProject = this.commonDataService.getSQWebProject();
    
    this.projectSellPrice = estimateMetaData?.estimateTab?.soldASSellPrice ?? 0;
    this.subcontractorCostPrice = otherMetaData?.ttsDetails?.subcontractorCost ?? 0;
    this.siteSize = laborMetaData?.taskBasedLaborSettings?.fireJobDetailsSiteSize ?? 0;

    // Calculate addressable devices and notification appliances from project areas
    this.calculatePointCounts(sqWebProject?.areas);

    if (laborMetaData) {
      this.fillReportInformation(laborMetaData);
    }
    return this.fireEstimatingData;
  }

  /**
   * Calculate total addressable devices and notification appliances from project areas
   */
  private calculatePointCounts(areas: SQWebArea[]): void {
    this.addressableDevices = 0;
    this.notificationAppliances = 0;
    if (!areas) return;
    areas.forEach(area => {
      if (area.isItemVisible) {
        this.addressableDevices += area.addressableDeviceCount ?? 0;
        this.notificationAppliances += area.audioVisualDeviceCount ?? 0;
        // Process children (systems)
        this.processChildrenForPointCounts(area.children as SQWebAreaChildren[]);
      }
    });
  }

  /**
   * Recursively process children to calculate point counts
   */
  private processChildrenForPointCounts(children: SQWebAreaChildren[]): void {
    if (!children) return;
    children.forEach(child => {
      if (child.nodeType === CONSTANTS.NODETYPE_AREA) {
        const area = child as SQWebArea;
        if (area.isItemVisible) {
          this.addressableDevices += area.addressableDeviceCount ?? 0;
          this.notificationAppliances += area.audioVisualDeviceCount ?? 0;
          this.processChildrenForPointCounts(area.children as SQWebAreaChildren[]);
        }
      } else if (child.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
        const system = child as SQWebSystem;
        if (system.isItemVisible) {
          this.addressableDevices += system.addressableDeviceCount ?? 0;
          this.notificationAppliances += system.audioVisualDeviceCount ?? 0;
        }
      }
    });
  }

  fillReportInformation(laborMetaData: LaborMetaData) {
    const estimateAnalysisTypes = new Map<string, { data: IData[], label: string, check: boolean }>([
      [FireEstimateAnalysisType.FIRE_ALARM, { 
        data: [], 
        label: 'FIRE_ALARM', 
        check: laborMetaData.taskBasedLaborSettings.isFireAlarmOfferingSelected 
      }],
      [FireEstimateAnalysisType.FIRE_SPRINKLER, { 
        data: [], 
        label: 'FIRE_SPRINKLER', 
        check: laborMetaData.taskBasedLaborSettings.isFireSprinklerOfferingSelected 
      }],
      [FireEstimateAnalysisType.GASEOUS_SYSTEM, { 
        data: [], 
        label: 'GASEOUS_SYSTEM', 
        check: laborMetaData.taskBasedLaborSettings.isGasSystemOfferingSelected 
      }],
      [FireEstimateAnalysisType.ERCES, { 
        data: [], 
        label: 'ERCES', 
        check: laborMetaData.taskBasedLaborSettings.isERCESOfferingSelected 
      }]
    ]);

    // Process each offering
    estimateAnalysisTypes.forEach((offering, key) => {
      this.addPricePerSqFtRow(offering.data); // Add common Price Per SqFt row for each Estimate Analysis Types
      if (offering.check) {
        switch (key) {
          case FireEstimateAnalysisType.FIRE_ALARM:
        this.getFireAlarmReportData(offering.data);
        break;
          case FireEstimateAnalysisType.FIRE_SPRINKLER:
        this.getFireSprinklerReportData(laborMetaData, offering.data);
        break;
          case FireEstimateAnalysisType.GASEOUS_SYSTEM:
        this.getGasSystemReportData(laborMetaData, offering.data);
        break;
          case FireEstimateAnalysisType.ERCES:
        this.getErcesReportData(laborMetaData, offering.data);
        break;
        }
        this.createFireEstimatingTable(offering.label, offering.data);
      }
    });
  }

  private createFireEstimatingTable(title: string, tableData: IData[]) {
      this.fireEstimatingData.push({
        tableHeader: this.reportsCommonService.generateReportTableHeader(title),
        title: [
          { data: this.translate.instant('TENNANT_MCA_ADDITION'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.LEFT_ALIGNMENT, width: this.FIXED_FIRST_COLUMN_WIDTH },
          { data: this.translate.instant('DEVICES'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
          { data: this.translate.instant('PER'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT },
          { data: CONSTANTS.DOLLAR_SIGN, columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ONE, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR, alignment: CONSTANTS.CENTER_ALIGNMENT }
        ],
        tableFooter: [],
        data: tableData,
        isPageBreakAfter: false,
        isTableBordered: true
      });
    }

  private getFireSprinklerReportData(laborMetaData: LaborMetaData, fireSprinklerTableData: IData[]) {
    let sprinklerHeads = laborMetaData.taskBasedLaborSettings.sprinklerHeads;
    fireSprinklerTableData.push(
      this.createRowValueData(
        this.translate.instant('PRICE_PER_SPRINKLER_HEAD_SUBCONTRACTOR'),
        sprinklerHeads,
        this.formatCurrency(this.subcontractorCostPrice),
        this.calculatePricePerUnit(this.subcontractorCostPrice, sprinklerHeads)
      ),
      this.createRowValueData(
        this.translate.instant('PRICE_PER_SPRINKLER_HEAD'),
        sprinklerHeads,
        this.formatCurrency(this.projectSellPrice),
        this.calculatePricePerUnit(this.projectSellPrice, sprinklerHeads)
      )
    );
  }

  private getGasSystemReportData(laborMetaData: LaborMetaData, gaseousSystemTableData: IData[]) {
    let agentWeight = laborMetaData.taskBasedLaborSettings.fireJobDetailsAgentWeight;
    let cleanAgentVolSpace = laborMetaData.taskBasedLaborSettings.fireJobDetailsCleanAgentVolSpace;
    gaseousSystemTableData.push(
      this.createRowValueData(
        this.translate.instant('PRICE_PER_AGENT_WEIGHT_SUBCONTRACTOR'),
        agentWeight,
        this.formatCurrency(this.subcontractorCostPrice),
        this.calculatePricePerUnit(this.subcontractorCostPrice, agentWeight)
      ),
      this.createRowValueData(
        this.translate.instant('PRICE_PER_NET_VOLUME_SUBCONTRACTOR'),
        cleanAgentVolSpace,
        this.formatCurrency(this.subcontractorCostPrice),
        this.calculatePricePerUnit(this.subcontractorCostPrice, cleanAgentVolSpace)
      ),
      this.createRowValueData(
        this.translate.instant('PRICE_PER_AGENT_WEIGHT'),
        agentWeight,
        this.formatCurrency(this.projectSellPrice),
        this.calculatePricePerUnit(this.projectSellPrice, agentWeight)
      ),
      this.createRowValueData(
        this.translate.instant('PRICE_PER_CLEAN_AGENT_NET_VOLUME'),
        cleanAgentVolSpace,
        this.formatCurrency(this.projectSellPrice),
        this.calculatePricePerUnit(this.projectSellPrice, cleanAgentVolSpace)
      )
    );
  }

  private getErcesReportData(laborMetaData: LaborMetaData, ercesTableData: IData[]) {
    let antennaQty = laborMetaData.taskBasedLaborSettings.fireJobDetailsAntennaQty;
    ercesTableData.push(
      this.createRowValueData(
        this.translate.instant('PRICE_PER_ANTENNA_SUBCONTRACTOR'),
        antennaQty,
        this.formatCurrency(this.subcontractorCostPrice),
        this.calculatePricePerUnit(this.subcontractorCostPrice, antennaQty)
      ),
      this.createRowValueData(
        this.translate.instant('PRICE_PER_ANTENNA_SELL'),
        antennaQty,
        this.formatCurrency(this.projectSellPrice),
        this.calculatePricePerUnit(this.projectSellPrice, antennaQty)
      )
    );
  }

  /**
   * Add Fire Alarm report data rows
   * Calculates devices from addressable devices + notification appliances
   */
  private getFireAlarmReportData(fireAlarmTableData: IData[]) {
    const devices = this.addressableDevices + this.notificationAppliances;
    fireAlarmTableData.push(
      this.createRowValueData(
        this.translate.instant('PRICE_PER_DEVICE_SUBCONTRACTOR'),
        String(devices),
        this.formatCurrency(this.subcontractorCostPrice),
        this.calculatePricePerUnit(this.subcontractorCostPrice, devices)
      ),
      this.createRowValueData(
        this.translate.instant('PRICE_PER_DEVICE_SELL'),
        String(devices),
        this.formatCurrency(this.projectSellPrice),
        this.calculatePricePerUnit(this.projectSellPrice, devices)
      )
    );
  }

  /**
   * Add Price Per SqFt row (common row for all offerings)
   * Calculates price per square foot based on project sell price and site size
   */
  private addPricePerSqFtRow(fireAlarmTableData: IData[]) {
    const pricePerSqFt = this.calculatePricePerUnit(this.projectSellPrice, this.siteSize);
    fireAlarmTableData.push(
      this.createRowValueData(
        this.translate.instant('PRICE_PER_SQFT_SELL'),
        CONSTANTS.DEFAULT_NOT_APPLICABLE_STRING,
        (String(this.siteSize)),
        pricePerSqFt
      )
    );
  }

  createRowValueData(
    label: string,
    value1: DataValue,
    value2: DataValue,
    value3: DataValue,
    alignment?: ReportTextAlignment
  ): IData {
    let rowData: IData = {
      data: [
        { data: label, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT, width: this.FIXED_FIRST_COLUMN_WIDTH },
        { data: this.formatValue(value1), columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: this.formatValue(value2), columnspan: 1, hierarchyLevel: 0, alignment: alignment ?? CONSTANTS.RIGHT_ALIGNMENT },
        { data: this.formatValue(value3), columnspan: 1, hierarchyLevel: 0, alignment: alignment ?? CONSTANTS.RIGHT_ALIGNMENT }
      ]
    };
    return rowData;
  }

  formatValue(value: DataValue): string {
    return typeof value === 'number' ? roundToPrecision(value,  this.commonDataService.getDecimalPrecision()).toString() : String(value);
  }

  formatCurrency(value: number): string {
    return this.multiLingualPipe.transform(value);
  }

  private calculatePricePerUnit(total: number, units: number): string {
    if (!units || !Number.isFinite(units)) {
      return this.formatCurrency(CONSTANTS.ZERO);
    }
    return this.formatCurrency(total / units);
  }

  ngOnDestroy(): void {
    this.clearFireEstimatingData();
  }

  private clearFireEstimatingData() {
    this.fireEstimatingData = [];
    this.projectSellPrice = 0;
    this.subcontractorCostPrice = 0;
    this.siteSize = 0;
    this.addressableDevices = 0;
    this.notificationAppliances = 0;
  }

}
